import { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { useParams, useNavigate } from 'react-router'

import * as actionTypes from '../../actions/actionTypes'
import { logoutAction } from '../../actions/userActions'
import { browserHistory } from '../../libs/historyHelper'
import { remove886 } from '../../libs/utils'
import {
  isCurrentItemExistSelector,
  isDiscountOnlySelector,
} from '../../reducers/itemEntryReducer'
import { isPurchaseAvailable } from '../../reducers/vendorReducer'
import NotFound from '../app/NotFound'
import ConfirmUserPage from '../buyer/ConfirmUserPage'
import LoadingOverlay from '../common/LoadingOverlay'
import ItemEntryDetail from '../item/ItemEntryDetail'
import useRedirectFromResetPassword from '../libs/hooks/useRedirectFromResetPassword'
import useRedirectToPaymentAfterVendorLogin from '../libs/hooks/useRedirectToPaymentAfterVendorLogin'
import { memberOrGuestIdSelector } from '../libs/selectors'
import BuyerLoginPage from './BuyerLoginPage'
import BuyerPaymentEntryPage from './BuyerPaymentEntryPage'
import DiscountOnlyLogoutScreen from './DiscountOnlyLogoutScreen'
import ErrorAifiOwnerOnlyScreen from './ErrorAifiOwnerOnlyScreen'

function BuyerItemRoute(props) {
  const {
    fetchEntity,
    itemKey,
    isDiscountOnly,
    isGuest,
    isIdentical,
    isBlockedByAIFIExclusive,
    logout,
    isActive,
    loggedIn,
  } = props
  const entity = props[itemKey]

  const { entityId } = useParams()
  const navigate = useNavigate()

  const [route, setRoute] = useState('entry')
  const redirectFromResetPassword = useRedirectFromResetPassword()
  const redirectToPaymentAfterVendorLogin =
    useRedirectToPaymentAfterVendorLogin()

  useEffect(() => {
    if (browserHistory.location?.state?.isPaymentFailed) {
      setRoute('payment')
      browserHistory.location.state.isPaymentFailed = false
    }
  }, [])

  useEffect(() => {
    if (isBlockedByAIFIExclusive) {
      logout()
      setRoute('aifiOwnerOnly')
    }
  }, [isBlockedByAIFIExclusive, logout])

  useEffect(() => {
    if (isIdentical) {
      alert('不能付款給自己，aifian 將自動幫你登出')
      logout()
      setRoute('entry')
    } else if (entityId) {
      fetchEntity(entityId)
    } else {
      navigate('/', { replace: true })
    }
  }, [isIdentical, entityId, fetchEntity, logout, navigate])

  useEffect(() => {
    if (isDiscountOnly && isGuest) {
      logout()
      setRoute('discountOnly')
    }
  }, [isDiscountOnly, isGuest, logout])

  useEffect(() => {
    if (redirectFromResetPassword()) {
      setRoute('login')
    }
  }, [redirectFromResetPassword, setRoute])

  const validateUsernameCallback = useCallback(
    username => {
      const userPhone = remove886(username)
      if (entity) {
        const sellerPhone = remove886(entity.contact)
        if (userPhone === sellerPhone) {
          return '不能付款給自己!'
        }
      }

      if (itemKey === 'buyerInstalments' && entity) {
        const originalBuyerPhone = remove886(entity.mobile)
        if (userPhone !== originalBuyerPhone) {
          return '你的手機號碼與訂單的手機不符，請重新輸入'
        }
      }
      return null
    },
    [itemKey, entity],
  )

  useEffect(() => {
    if (!isActive) {
      setRoute('entry')
    }
  }, [isActive, setRoute])

  useEffect(() => {
    if (redirectToPaymentAfterVendorLogin) {
      setRoute('payment')
    }
  }, [redirectToPaymentAfterVendorLogin])

  if (!props.isCurrentItemExist) {
    return <NotFound />
  }

  if (!entity) {
    return <LoadingOverlay />
  }

  switch (route) {
    case 'login':
      return (
        <BuyerLoginPage
          toConfirmUser={() => setRoute('confirmUser')}
          toPayment={() => setRoute('payment')}
          validateUsername={validateUsernameCallback}
        />
      )
    case 'confirmUser':
      return <ConfirmUserPage toPayment={() => setRoute('payment')} />
    case 'aifiOwnerOnly':
      return <ErrorAifiOwnerOnlyScreen />
    case 'payment':
      return (
        <BuyerPaymentEntryPage toPurchaseLoginPage={() => setRoute('login')} />
      )
    case 'discountOnly':
      return (
        <DiscountOnlyLogoutScreen
          toLogin={() => {
            logout()
            setRoute('entry')
          }}
        />
      )
    case 'entry':
    default:
      return (
        <ItemEntryDetail
          currentItem={entity}
          isServiceAvailable={props.isServiceAvailable}
          isActive={isActive}
          toLogin={() => {
            setRoute(loggedIn ? (isGuest ? 'confirmUser' : 'payment') : 'login')
          }}
        />
      )
  }
}

const ConnectedBuyerItemRoute = connect(
  (state, ownProps) => {
    const { itemKey } = ownProps
    return {
      [itemKey]: state.itemEntry[itemKey],
      isActive: state.landing.isActive && !state.landing.unavailable,
      isServiceAvailable:
        itemKey === 'buyerPeriodic' || isPurchaseAvailable(state.vendor),
      isCurrentItemExist: isCurrentItemExistSelector(state.itemEntry),
      isDiscountOnly: isDiscountOnlySelector(state.itemEntry),
      isIdentical: state.buyer.isIdentical,
      isBlockedByAIFIExclusive: state.buyer.isBlockedByAIFIExclusive,
      isGuest: !!state.sms.sentPhone,
      loggedIn: memberOrGuestIdSelector(state.user, state.sms),
    }
  },
  (dispatch, ownProps) => ({
    fetchEntity(entityId) {
      dispatch({
        type: ownProps.actionType,
        payload: entityId,
      })
    },
    logout() {
      dispatch(logoutAction())
    },
  }),
)(BuyerItemRoute)

export function BuyerItemRoutePage() {
  return (
    <ConnectedBuyerItemRoute
      itemKey="item"
      actionType={actionTypes.GET_ITEM_ENTRY_INFO}
    />
  )
}

export function BuyerInstalmentRoutePage() {
  return (
    <ConnectedBuyerItemRoute
      itemKey="buyerInstalments"
      actionType={actionTypes.GET_BUYER_INSTALMENTS_INFO}
    />
  )
}

// 「逾期定期定額付款」
export function BuyerPeriodicRoutePage() {
  return (
    <ConnectedBuyerItemRoute
      itemKey="buyerPeriodic"
      actionType={actionTypes.GET_BUYER_PERIODIC_INFO}
    />
  )
}
